<template>
<h1>Academics</h1>
<h2>PHY 481 Capstone Project: An Analysis of Numerical Integration Methods</h2>
<h3>Overview</h3>
<div class="textbody">
<p>The project is a cross comparison of numerical integration methods. The project is broadly theory focussed, with the paper primarily giving clear definitions, mathematical derivations, and uses for the integration algorithms. Specific simulation results are largely secondary to the theory, and are used to back up the theory claims and give concrete comparisons of the algorithms.</p>
<br/>
<p><a href="https://drive.google.com/drive/folders/1NciKNrfCN81iKVnWh0wpgwYSDUxZTFhk?usp=sharing">Here is a link to a Google Drive containing all the project files, including the final report.</a></p>
</div>
<div class="textbody">
<p>The paper starts off with some foundational definitions in error and convergence, then gives methods of evaluating and improving sequence convergenceand series convergence. These methods include linear regression, Lagrange interpolating polynomials, Richardson Extrapolation, Van Wijngaarden Transformation, Aitken Extrapolation, and Wynn's Epsilon Method. Example results of these methods are given alongside the definitions, and these are then used at times when experimentally evaluating the numerical integration methods.</p>
<p>Next described and explored are the numerical integration methods. First there are numerical quadrature methods, which include Reimann integrals, Simpson's rule, Romberg's method, Newton-Cotes quadrature, and Gaussian quadrature. Next are sampling methods, including Monte Carlo Sampling, Markov Chain Monte Carlo, and Metropolis-Hastings algorithm.</p>
<p>With the algorithms defined and established, the algorithms are then more thoroughly evaluated, tested, and compared, with the results of such being presented. The algorithms are coded in c++, run on a personal computer, the results are tabulated and analyzed in R, and then formatted and presented in the paper. Conclusions are then drawn, in comparing the numerical results of the algorithms to their theoretically derived rates of convergence, and in comparing the algorithms to one another.</p>
</div>
<h3>Preview</h3>
<p>Here are a few example images from the paper. This section will likely be removed as the paper itself becomes fully available.</p>
<img src="../assets/figure_2.png" alt="A sample image of Figure 2 from the paper.">
<img src="../assets/figure_4.png" alt="A sample image of Figure 4 from the paper.">
<img src="../assets/figure_6.png" alt="A sample image of Figure 6 from the paper.">
<img src="../assets/figure_7.png" alt="A sample image of Figure 7 from the paper.">
<h3>Resources</h3>
<p>This section will contain links to the paper, the presentation, and the GitHub repository. I'm holding off on linking those now as these are each currently incomplete.</p>
<p>The paper is written in LaTeX (article document type), and the presentation is also written in LaTeX (beamer document type).</p>
<p>The codebase is split among a few small files to run specific algorithms and demonstrations. All code is written in c++ and does not make use of any external (non-standard) libraries.</p>
<p>The outputs of the code is analyzed and passed through R scripts/notebooks, which also generate the plots of the data as images.</p>
<h3>History</h3>
<div class="textbody">
<p>The project started as an Honors Thesis for a Math and Physics dual degree, started in 2019 and planned to be complete in 2020. The project goal started off a bit different (and lofty), being to research methods of classifying the topologies of phase spaces of simulations, and then using such a lens of analysis to make conclusions about convergence of algorithms. While the research built up, the writing shifted towards focussing on the numerical integration methods that were needed for the original scope of the project, and that eventually turned into the primary focus.</p>
<p>A bit of a difficulty that started to be encountered was, essentially, with a textbook found that was too good of a resource. The text was rich with exactly the theory and calculations and examples wanted for the project. While that would have been fine and great within the original extended scope of the project, with the project shifting towards just being about the numerical integration methods it was becoming far too similar to what the text accomplished (and accomplished better). This was also compounded by the honors thesis requiring at least 10 references, but that one text was able to supply particularly well for the whole project.</p>
<p>To remedy this, the project needed to shift towards looking at and involving more varied examples of experimental results from various papers to meet the 10 references quota, which was at odds with my general inclination towards the mathematical and theoretical physics wants for the paper. Ultimately, despite having a lot of content, the thesis needed some reworking to accommodate around the encountered and shifting difficulties, and overall the project was also losing interest for me from having shifted in goals and scope so much.</p>
<p>The thesis presentation was initially just delayed to Summer 2020 (for a Summer graduation), but the progress and work never fully picked up again to be completed in time. This was of course all thoroughly made more difficult due to the fact that COVID-19 had just started at that second semester, making things all the more removed.</p>
<p>Overall, the project has gone through a lot of changes, and so have I. I’ve now settled to complete the project simply as a physics capstone, aiming to complete a different honors thesis purely in math at some point. I’m not entirely satisfied with the project being what it is, but I likely would never fully be. It still has been very nice to get closure on this project though. It has been nice to be back in academia again, and obtaining a degree at this point also serves a bit of utility in my life.</p>
</div>
</template>


<script>

</script>


<style scoped>
h1 {
  font-size: 3em;
}
h2 {
  text-align: center;
  text-decoration: underline;
  font-size: 2em;
}
h3 {
  text-align: left;
  font-size: 1.5em;
  padding-left: 4em;
  text-decoration: underline;
}
.textbody {
  width: calc(100% - 12em);
  display: inline-block;
  text-align: center;
  border-width: 8px;
  border-radius: 30px;
  border-color: rgba(0,128,128,0.3);
  border-left-style: solid;
  border-right-style: solid;
  padding: 0em 4em 0em 4em;
  margin-bottom: 1em
}
.textbody p {
  text-indent: 2em;
  text-align: left;
  font-size: 1.5em;
  margin: 2em 1em;
  padding-left: 0.5em;
  /*border-left: 4px double rgba(128,0,128,0.3);*/
  /*border-right: 4px solid black;*/
}
p {
  text-indent: 2em;
  text-align: left;
  font-size: 1.5em;
  margin: 1em 1em;
  padding-left: 1.5em;
}
img {
  width: 40%;
  margin: 1em;
  margin-bottom: 2em;
}
</style>